import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, MapPin, Ticket as TicketIcon, ChevronRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/tickets/")({
  head: () => ({ meta: [{ title: "Biletlerim — JUËN" }] }),
  component: MyTicketsPage,
});

interface TicketWithDetails {
  id: string;
  code: string;
  created_at: string;
  attendee_name: string | null;
  events: { id: string; name: string; starts_at: string; venue: string | null } | null;
  ticket_types: { name: string } | null;
}

function MyTicketsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [tickets, setTickets] = useState<TicketWithDetails[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const tc = useAutoTranslate(tickets.flatMap((tk) => [tk.events?.name, tk.events?.venue, tk.ticket_types?.name]));

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data } = await supabase
        .from("tickets")
        .select("id,code,created_at,attendee_name,events(id,name,starts_at,venue),ticket_types(name)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      setTickets((data as unknown as TicketWithDetails[]) ?? []);
      setPageLoading(false);
    })();
  }, [user]);

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/dashboard" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("account.title")}
        </Link>

        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("account.tickets.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("account.tickets.subtitle")}</p>

        {tickets.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center">
            <TicketIcon className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">{t("account.tickets.empty")}</p>
            <Button asChild className="mt-4">
              <Link to="/events">{t("account.browseEvents")}</Link>
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {tickets.map((tk) => (
              <Link
                key={tk.id}
                to="/tickets/$ticketId"
                params={{ ticketId: tk.id }}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <TicketIcon className="h-4 w-4 text-primary" />
                    <h3 className="truncate text-lg font-semibold">{tc(tk.events?.name) ?? t("account.fallbackEvent")}</h3>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {tk.events && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(tk.events.starts_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    )}
                    {tk.events?.venue && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {tc(tk.events.venue)}
                      </span>
                    )}
                  </div>
                  {tk.ticket_types && (
                    <div className="mt-1 text-xs font-medium text-foreground/80">
                      {tc(tk.ticket_types.name)}
                      {tk.attendee_name && ` · ${tk.attendee_name}`}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
